import { defineStore } from 'pinia'
import { usePlansStore } from './plans'
import type { Router, RouteLocationNormalizedLoaded } from 'vue-router'
import { z } from '@plt/types'
import {
  PLANS_QUERY_DEFAULTS,
  VALIDATION_LIMITS,
  VALIDATION_PATTERNS,
  VALID_SORT_BY_OPTIONS,
  VALID_FILTER_OPTIONS,
} from '../constants/plansQuery'

interface PlansQueryState {
  sortBy: string
  sortOrder: 'asc' | 'desc'
  filter: string
  search: string
}

const queryParamsSchema = z.object({
  sort_by: z.enum(VALID_SORT_BY_OPTIONS).optional(),
  sort_order: z.enum(['asc', 'desc']).optional(),
  filter: z.enum(VALID_FILTER_OPTIONS).optional(),
  search: z.string().max(VALIDATION_LIMITS.SEARCH_MAX_LENGTH).regex(VALIDATION_PATTERNS.SEARCH_INPUT).optional(),
})

type ValidatedQueryParams = z.infer<typeof queryParamsSchema>

/** Validates one field in isolation, so an invalid value falls back for that field alone. */
const parseField = <T>(schema: z.ZodTypeAny, value: unknown): T | undefined => {
  const result = schema.safeParse(value)
  return result.success ? (result.data as T) : undefined
}

interface RouteQueryParams {
  sort_by?: string
  sort_order?: 'asc' | 'desc'
  filter?: string
  search?: string
}

interface ApiQueryParams {
  sort_by: string
  sort_order: 'asc' | 'desc'
  filter?: string
  search?: string
}

const LEGACY_CURSOR_KEYS = ['last_evaluated_id', 'last_evaluated_sort', 'last_evaluated_scope'] as const

type RouteQuery = RouteLocationNormalizedLoaded['query']

const withoutLegacyCursorKeys = (query: RouteQuery): RouteQuery => {
  const cleaned = { ...query }
  LEGACY_CURSOR_KEYS.forEach(key => delete cleaned[key])
  return cleaned
}

const hasLegacyCursorKeys = (query: RouteQuery): boolean => LEGACY_CURSOR_KEYS.some(key => key in query)

export const usePlansQueryStore = defineStore('plansQuery', {
  state: (): PlansQueryState => ({
    sortBy: PLANS_QUERY_DEFAULTS.SORT_BY,
    sortOrder: PLANS_QUERY_DEFAULTS.SORT_ORDER,
    filter: PLANS_QUERY_DEFAULTS.FILTER,
    search: '',
  }),

  getters: {
    apiQueryParams: (state): Partial<ApiQueryParams> => {
      const params: Partial<ApiQueryParams> = {
        sort_by: state.sortBy,
        sort_order: state.sortOrder,
        filter: state.filter !== PLANS_QUERY_DEFAULTS.FILTER ? state.filter : undefined,
      }

      if (state.search.trim()) {
        params.search = state.search.trim()
      }

      return Object.fromEntries(
        Object.entries(params).filter(([, value]) => value !== undefined),
      ) as Partial<ApiQueryParams>
    },

    routeQueryParams: (state): RouteQueryParams => {
      const params: RouteQueryParams = {
        sort_by: state.sortBy,
        sort_order: state.sortOrder,
      }

      if (state.filter !== PLANS_QUERY_DEFAULTS.FILTER) {
        params.filter = state.filter
      }

      if (state.search) {
        params.search = state.search
      }

      return params
    },

    hasDefaultSort: state => {
      return state.sortBy === PLANS_QUERY_DEFAULTS.SORT_BY && state.sortOrder === PLANS_QUERY_DEFAULTS.SORT_ORDER
    },
  },

  actions: {
    syncFromRoute(route: RouteLocationNormalizedLoaded) {
      const sortBy = parseField<ValidatedQueryParams['sort_by']>(queryParamsSchema.shape.sort_by, route.query.sort_by)
      const sortOrder = parseField<ValidatedQueryParams['sort_order']>(
        queryParamsSchema.shape.sort_order,
        route.query.sort_order,
      )
      const filter = parseField<ValidatedQueryParams['filter']>(queryParamsSchema.shape.filter, route.query.filter)
      const search = parseField<ValidatedQueryParams['search']>(queryParamsSchema.shape.search, route.query.search)

      this.sortBy = sortBy || PLANS_QUERY_DEFAULTS.SORT_BY
      this.sortOrder = sortOrder || PLANS_QUERY_DEFAULTS.SORT_ORDER
      this.filter = filter || PLANS_QUERY_DEFAULTS.FILTER
      this.search = search || ''

      this.resetPagination()
    },

    async updateFilter(router: Router, route: RouteLocationNormalizedLoaded, filter: string) {
      this.filter = filter
      this.search = ''
      this.resetPagination()
      await this.syncToRoute(router, route)
    },

    async updateSearch(router: Router, route: RouteLocationNormalizedLoaded, search: string) {
      this.search = search
      this.resetPagination()
      await this.syncToRoute(router, route)
    },

    resetPagination() {
      usePlansStore().resetCursor()
    },

    async syncToRoute(router: Router, route: RouteLocationNormalizedLoaded) {
      const newQuery = { ...withoutLegacyCursorKeys(route.query), ...this.routeQueryParams }

      if (this.filter === PLANS_QUERY_DEFAULTS.FILTER) {
        delete newQuery.filter
      }

      if (!this.search) {
        delete newQuery.search
      }

      if (route.name === 'plans') {
        await router.replace({ query: newQuery })
      } else {
        await router.push({ name: 'plans', query: newQuery })
      }
    },

    async ensureDefaultSortParams(router: Router, route: RouteLocationNormalizedLoaded) {
      if (!route.query.sort_by || !route.query.sort_order || hasLegacyCursorKeys(route.query)) {
        await router.replace({
          query: {
            ...withoutLegacyCursorKeys(route.query),
            sort_by: this.sortBy,
            sort_order: this.sortOrder,
          },
        })
        return true
      }
      return false
    },
  },
})
